import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Mic, MicOff, ChevronsLeft, ChevronsRight, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useLmxStore } from './lmxStore';
import { lmxAttention, ATTENTION_ICON, ATTENTION_LABEL, statusLine, fmtElapsed } from './semaforo';
import { useLmxTurn } from './useLmxTurn';
import { LmxCard, isEmptyCard } from './LmxCards';
import { LmxJobs } from './LmxJobs';
import { nextToDispatch, ACTIVE as JOB_ACTIVE } from './trabajos';
import { LmxDockEntry } from './LmxDockEntry';
import { toolOf } from './toolOf';
import { LmxText } from './LmxText';
import { useLmxAttach, LmxAttachButton, LmxAttachTray } from './LmxAttach';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import { useWorkflowStore } from '../store/workflowStore';
import { useCanvasNavStore } from '../store/canvasNavStore';
import useTimelineStore from '../store/timelineStore';
import { useRevealStore } from '../store/revealStore';
import './lmx.css';
import './lmx-dock.css';
// Envío UI-2 · LMX como columna fija a la derecha (layout nuevo, detrás de flag)
import { LAYOUT_V2, LMX_COL_OPEN, LMX_COL_RAIL } from '../layout/layoutV2';

const API = process.env.REACT_APP_BACKEND_URL;
// E6f · recorrido de LMX. REACT_APP_LMX_TOUR=false lo apaga del todo.
const LMX_TOUR = String(process.env.REACT_APP_LMX_TOUR ?? 'true') !== 'false';
const H_KEY = 'lmx.dock.height';
const H_MIN = 140;
const H_MAX_RATIO = 0.6;
const STRIP_H = 40;
// Envío LMX-1 · si el canvas no pudo traer los cambios de LMX (guardado en
// curso o falla de red), se reintenta en vez de dejarlos fuera de vista (I-18).
const SYNC_RETRIES = 4;
const SYNC_RETRY_MS = 1500;
// El borde para estirar el timeline sobresale 3 px hacia arriba (timeline.css):
// el dock se apoya 3 px más arriba para no taparlo.
const TL_HANDLE_GAP = 3;
// Tools que cambian el canvas: al terminar bien, el canvas abierto se sincroniza.
const CANVAS_TOOLS = new Set([
  'add_node', 'configure_node', 'connect_nodes', 'disconnect_nodes', 'remove_node',
  'create_workflow', 'upload_asset', 'set_text',
  // Envío PJ-1 · aplicar un personaje y la hoja de modelo tocan el canvas.
  'apply_character', 'create_character_sheet',
]);

// Tools que cambian el TIMELINE: al terminar bien, el editor abierto se
// sincroniza con el servidor (destello en los clips/elementos tocados).
// `set_text` también toca el timeline (texto de plantillas): está en ambos.
const TIMELINE_TOOLS = new Set([
  'add_clips', 'update_clip', 'remove_clip', 'move_clip', 'set_track_order',
  'add_elements', 'update_element', 'remove_element', 'fill_slot', 'set_text',
  'create_timeline_from_template',
]);

// Envío LMX-5 · sugerencias en el chat vacío de la columna de LMX.
const LMX_STARTERS = [
  { label: '¿Qué podés hacer?', text: '¿Qué podés hacer por mí?' },
  { label: 'Creá mi marca', text: 'Quiero crear la marca de mi negocio' },
  { label: 'Usar una plantilla', text: 'Mostrame mis plantillas y usemos una' },
  { label: 'Publicar en redes', text: '¿Cómo publico en mis redes con Buffer?' },
  { label: 'Usarlo desde el celular', text: '¿Cómo uso Studio Media Labs desde Claude en el celular?' },
];

const lsGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const lsSet = (k, v) => { try { localStorage.setItem(k, v); } catch { /* cuota */ } };

/**
 * LmxDock — LMX acoplado abajo, como una solapa apoyada sobre el timeline.
 *
 * Reutiliza TODO lo que ya existe (lmxStore, useLmxTurn, LmxCard): sólo cambia
 * la forma de mostrarlo. No toca TimelineEditor: lo observa en el DOM y se
 * apoya justo encima de su borde superior (o al fondo si está cerrado).
 *
 *  · plegado  → renglón con el último mensaje, campo para escribir y el orbe
 *               medio asomado en su cuna, latiendo.
 *  · abierto  → además, cuerpo con conversación (izq.) y tarjetas (der.).
 *  · entrada  → telón con el orbe grande al centro (una vez por sesión).
 */
export const LmxDock = () => {
  const { getToken } = useAuth();
  const {
    mode, available, busy, notify, turn, log, pendingConfirm, status, assets, veil, prefs,
    attachments, jobs, runs,
  } = useLmxStore();
  const attach = useLmxAttach();
  const readyAtts = attachments.filter((a) => a.status === 'ready');
  const uploadingAtts = attachments.some((a) => a.status === 'uploading');
  const store = useLmxStore;
  const { send, approve, cancel } = useLmxTurn();
  // OJO: el store se llama currentProjectId (LmxLayer leía `projectId`, que no existe).
  const projectId = useWorkflowStore((s) => s.currentProjectId);
  const flash = useCanvasNavStore((s) => s.flash);
  const setSearchOpen = useCanvasNavStore((s) => s.setSearchOpen);
  const requestFocus = useCanvasNavStore((s) => s.requestFocus);
  const timelineOpen = useTimelineStore((s) => s.isOpen);

  const open = mode === 'open';
  const [text, setText] = useState('');
  const [pinned, setPinned] = useState(false);
  const [tlHeight, setTlHeight] = useState(0);
  const [bodyH, setBodyH] = useState(() => {
    const v = parseInt(lsGet(H_KEY), 10);
    return Number.isFinite(v) && v >= H_MIN ? v : 240;
  });
  const convoRef = useRef(null);
  const dragRef = useRef(null);
  const tokenRef = useRef(getToken);
  tokenRef.current = getToken;

  // ── Dictado por voz (misma base que el mic de los nodos) ──
  const speech = useSpeechRecognition({ lang: 'es-AR' });
  const speechBaseRef = useRef('');

  // Mientras escucha, el texto dictado se suma a lo que ya estaba escrito.
  useEffect(() => {
    if (!speech.listening || !speech.transcript) return;
    const base = speechBaseRef.current;
    setText(base ? `${base.replace(/\s+$/, '')} ${speech.transcript}` : speech.transcript);
  }, [speech.transcript, speech.listening]);

  useEffect(() => {
    if (!speech.error) return;
    toast.error('No pude usar el micrófono. Revisá el permiso del navegador.');
  }, [speech.error]);

  const toggleMic = useCallback(() => {
    if (!speech.supported) {
      toast.info('Tu navegador no permite dictar. Probá con Chrome o Edge.');
      return;
    }
    if (speech.listening) { speech.stop(); return; }
    speechBaseRef.current = text;
    speech.reset();
    speech.start();
  }, [speech, text]);

  const stopMic = useCallback(() => {
    if (speech.listening) speech.stop();
  }, [speech]);

  // ── Entrada: telón con el orbe al centro, una vez por sesión ──
  useEffect(() => {
    let seen = false;
    try { seen = sessionStorage.getItem('lmx-dock-entry') === '1'; } catch { /* privado */ }
    store.setState({ mode: seen ? 'docked' : 'curtain' });
  }, [store]);
  const leaveCurtain = useCallback(() => {
    try { sessionStorage.setItem('lmx-dock-entry', '1'); } catch { /* privado */ }
    store.getState().open();
  }, [store]);

  // ── Rehidratación: cada proyecto tiene SU conversación ──
  // Al cambiar de proyecto se vacía lo que se ve (y el conversationId, para que
  // el próximo pedido no siga la charla del proyecto anterior) y se trae la del
  // proyecto nuevo, si existe.
  const lastProjectRef = useRef(projectId);
  useEffect(() => {
    if (!available || !API) return undefined;
    if (lastProjectRef.current !== projectId) {
      lastProjectRef.current = projectId;
      if (!store.getState().busy) {
        store.setState({
          log: [], turn: { text: '', cards: [] }, conversationId: null, pendingConfirm: null,
          jobs: null, runs: [],
        });
      }
    }
    // Lienzo sin proyecto guardado: arranca vacío (nunca la charla de otro).
    if (!projectId) return undefined;
    let alive = true;
    (async () => {
      try {
        const token = await tokenRef.current();
        const url = `${API}/api/lmx/conversation?project_id=${projectId}`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok || !alive) return;
        store.getState().hydrate(await res.json());
      } catch { /* sin historial */ }
    })();
    return () => { alive = false; };
  }, [available, projectId, store]);

  // ── El timeline: lo observamos (sin tocarlo) para apoyarnos encima ──
  useEffect(() => {
    if (LAYOUT_V2) return undefined; // UI-2: la columna no se apoya sobre el timeline
    let ro = null;
    let watched; // undefined: fuerza la primera medición (también cuando el timeline ya está cerrado)
    const attach = () => {
      const el = document.querySelector('[data-testid="timeline-editor"]');
      if (el === watched) return;
      if (ro) { ro.disconnect(); ro = null; }
      watched = el;
      if (!el) { setTlHeight(0); return; } // timeline cerrado → el dock baja al fondo
      setTlHeight(el.offsetHeight);
      if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(() => setTlHeight(el.offsetHeight));
        ro.observe(el);
      }
    };
    attach();
    const mo = new MutationObserver(attach);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => { mo.disconnect(); if (ro) ro.disconnect(); };
  }, [timelineOpen]);

  // Si el timeline SE ABRE grande y no fijaste el chat, el chat se pliega.
  // Sólo al abrirlo: estirarlo con el chat abierto no lo pliega.
  const prevTlOpen = useRef(timelineOpen);
  useEffect(() => {
    const opened = timelineOpen && !prevTlOpen.current;
    prevTlOpen.current = timelineOpen;
    if (LAYOUT_V2) return undefined; // UI-2: el timeline nunca tapa la columna
    if (!opened || pinned || !open) return undefined;
    const t = setTimeout(() => {
      const el = document.querySelector('[data-testid="timeline-editor"]');
      if (el && el.offsetHeight > window.innerHeight * 0.4) store.getState().collapse();
    }, 80);
    return () => clearTimeout(t);
  }, [timelineOpen, pinned, open, store]);

  // ── Canvas en vivo ──
  // Cada tool de canvas que termina OK trae los cambios del agente al canvas
  // abierto SIN pisar lo que el usuario tenga sin guardar (syncFromAgent hace
  // un merge a 3 vías; si hay choque real, cae al modal de conflicto de siempre).
  // Los nodos NUEVOS aparecen con la animación de Lumière (WorkflowRevealOverlay:
  // vuelan desde la paleta a su lugar) y la cámara encuadra el flujo.
  // Los nodos CAMBIADOS se centran y parpadean.
  const seenToolsRef = useRef(new Set());
  const touchedRef = useRef([]);
  const syncTimerRef = useRef(null);
  const tlSyncTimerRef = useRef(null);

  // Timeline en vivo: una tool de timeline que terminó OK → UN pedido al
  // servidor (debounce 350 ms, igual que el canvas). Sólo con el editor
  // abierto; el destello lo pone el store (agentFlash).
  const syncTimeline = useCallback(async () => {
    const tl = useTimelineStore.getState();
    if (!tl.isOpen || !projectId) return;
    await tl.syncTimelineFromAgent(projectId);
  }, [projectId]);

  const syncCanvasRef = useRef(null);
  const syncCanvas = useCallback(async (attempt = 0, carried = null) => {
    const ws = useWorkflowStore.getState();
    const touched = carried || touchedRef.current;
    if (!carried) touchedRef.current = [];
    const canvasMounted = !!document.querySelector('.react-flow');
    if (!ws.syncFromAgent) { // respaldo: el mecanismo anterior
      await ws.checkProjectVersion?.();
      if (touched.length) flash?.(touched[touched.length - 1]);
      return;
    }
    let revealed = false;
    const res = await ws.syncFromAgent({
      // Se llama en el MISMO tick en que los nodos entran al store: así nunca
      // se ven "de golpe" antes de la animación.
      onNew: ({ nodeIds, edgeIds }) => {
        if (!canvasMounted || !nodeIds.length) return;
        const rv = useRevealStore.getState();
        if (rv.active) return; // ya hay una animación en curso: estos aparecen directo
        rv.start({ nodeIds, edgeIds, frame: 'targets' });
        revealed = true;
      },
    });
    // Los cambios del agente YA están en el servidor: si no se pudieron traer
    // (guardado en curso, red), reintentar. Un conflicto real lo resuelve el
    // modal de siempre, por eso no se reintenta.
    if (res && !res.ok && !res.conflict) {
      if (attempt < SYNC_RETRIES) {
        clearTimeout(syncTimerRef.current);
        syncTimerRef.current = setTimeout(
          () => syncCanvasRef.current?.(attempt + 1, touched), SYNC_RETRY_MS);
      } else {
        toast.warning('LMX cambió el proyecto pero no pude mostrarlo en el canvas. '
          + 'Guardá y recargá la página para ver sus cambios.');
      }
      return;
    }
    // Sólo si de verdad cambió algo (la sincronización de cierre de turno no
    // tiene que soltar lo que el usuario tenga seleccionado).
    if (res?.ok && canvasMounted && ((res.newNodeIds || []).length || touched.length)) {
      // La selección del usuario atenúa todo lo que no es su cadena: después de
      // un cambio de LMX se suelta, para que lo nuevo se vea con su color.
      useWorkflowStore.getState().setSelectedNode?.(null);
    }
    if (!res?.ok || revealed || !canvasMounted) return;
    // Sin animación (nodos cambiados, o llegaron durante otra): centrar y parpadear.
    const fresh = res.newNodeIds || [];
    const id = fresh.length ? fresh[fresh.length - 1] : touched[touched.length - 1];
    if (!id) return;
    requestFocus(id);
    setTimeout(() => flash?.(id), 420);
  }, [flash, requestFocus]);
  syncCanvasRef.current = syncCanvas;

  useEffect(() => {
    const rows = turn.cards.filter((c) => c.type === 'progress').flatMap((c) => c.items || []);
    let touched = false;
    let tlTouched = false;
    rows.forEach((it) => {
      if (it.state !== 'ok' || seenToolsRef.current.has(it.id)) return;
      seenToolsRef.current.add(it.id);
      const tool = toolOf(it);
      if (CANVAS_TOOLS.has(tool)) {
        touched = true;
        if (/^node_\d+$/.test(it.meta || '')) touchedRef.current.push(it.meta);
      }
      if (TIMELINE_TOOLS.has(tool)) tlTouched = true;
    });
    if (touched) {
      clearTimeout(syncTimerRef.current);
      syncTimerRef.current = setTimeout(syncCanvas, 350);
    }
    if (tlTouched) {
      clearTimeout(tlSyncTimerRef.current);
      tlSyncTimerRef.current = setTimeout(syncTimeline, 350);
    }
  }, [turn.cards, syncCanvas, syncTimeline]);
  useEffect(() => () => {
    clearTimeout(syncTimerRef.current);
    clearTimeout(tlSyncTimerRef.current);
  }, []);

  // I-41 · red de seguridad: al cerrar el turno (o si el stream se cortó y la
  // charla se recargó) se trae una vez lo que haya en el servidor. Es barato:
  // canvas y timeline sólo cambian si el servidor tiene una versión nueva.
  useEffect(() => {
    const onClosed = (e) => {
      if (e?.detail?.projectId && projectId && e.detail.projectId !== projectId) return;
      clearTimeout(syncTimerRef.current);
      clearTimeout(tlSyncTimerRef.current);
      syncTimerRef.current = setTimeout(() => syncCanvasRef.current?.(), 500);
      tlSyncTimerRef.current = setTimeout(syncTimeline, 500);
    };
    window.addEventListener('lmx:turn-closed', onClosed);
    window.addEventListener('lmx:rehydrate', onClosed);
    return () => {
      window.removeEventListener('lmx:turn-closed', onClosed);
      window.removeEventListener('lmx:rehydrate', onClosed);
    };
  }, [projectId, syncTimeline]);

  // El canvas nunca queda debajo del renglón plegado.
  useEffect(() => {
    if (!available) return undefined;
    const cls = LAYOUT_V2 ? 'lmx-col-on' : 'lmx-dock-on';
    document.body.classList.add(cls);
    return () => document.body.classList.remove(cls);
  }, [available]);

  // UI-2 · ancho de la columna (abierta o riel) para que el resto se acomode.
  useEffect(() => {
    if (!LAYOUT_V2 || !available) return undefined;
    const root = document.documentElement;
    root.style.setProperty('--lmx-col-w', `${open ? LMX_COL_OPEN : LMX_COL_RAIL}px`);
    return () => root.style.removeProperty('--lmx-col-w');
  }, [open, available]);

  // Con el dock abierto (o apoyado sobre el timeline) los controles del canvas
  // (zoom, encuadrar, minimapa) suben para quedar siempre a la vista.
  const dockRef = useRef(null);
  useEffect(() => {
    const el = dockRef.current;
    if (!el || LAYOUT_V2) return undefined; // UI-2: nada flota sobre el canvas
    const root = document.documentElement;
    const apply = () => {
      const lift = el.offsetHeight + (tlHeight ? tlHeight + TL_HANDLE_GAP : 0) - STRIP_H;
      const cap = Math.round(window.innerHeight * 0.6);
      root.style.setProperty('--lmx-dock-lift', `${Math.max(0, Math.min(lift, cap))}px`);
    };
    apply();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    ro?.observe(el);
    window.addEventListener('resize', apply);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', apply);
      root.style.removeProperty('--lmx-dock-lift');
    };
  }, [tlHeight, available]);

  // Esc pliega · evento lmx:open abre
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || store.getState().mode !== 'open') return;
      if (document.activeElement?.dataset?.testid === 'lmx-dock-input') return;
      store.getState().collapse();
    };
    const onOpen = () => store.getState().open();
    window.addEventListener('keydown', onKey);
    window.addEventListener('lmx:open', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('lmx:open', onOpen);
    };
  }, [store]);

  // Envío LMX-6b · la columna sigue lo que pasa (texto, filas de herramientas,
  // tarjetas), salvo que el usuario haya subido a leer algo anterior.
  const stickRef = useRef(true);
  const onConvoScroll = useCallback((e) => {
    const el = e.currentTarget;
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);
  useEffect(() => {
    const el = convoRef.current;
    if (el && stickRef.current) el.scrollTop = el.scrollHeight;
  }, [log, turn.text, turn.cards, busy]);

  // Envío LMX-6b · avance de la ejecución en el Canvas (lo publica
  // McpRunOverlay con el sondeo que ya existe) y aviso al terminar.
  const [runInfo, setRunInfo] = useState(null);
  const runSeenRef = useRef(null);
  useEffect(() => {
    const onRun = (e) => {
      const { projectId: pid, run, runs: all } = e.detail || {};
      if (pid !== projectId) return;
      store.getState().setRuns(all || (run ? [run] : []));
      if (run) {
        // Envío LMX-T · con dos trabajos generando, el avance suma las corridas.
        const list = all && all.length ? all : [run];
        const total = list.reduce((n, r) => n + ((r.steps || []).length || 0), 0);
        const done = list.reduce((n, r) => n + (r.completed_nodes || []).length, 0);
        runSeenRef.current = { total, done };
        setRunInfo({ total, done });
        return;
      }
      const seen = runSeenRef.current;
      runSeenRef.current = null;
      setRunInfo(null);
      if (seen && seen.total) {
        store.getState().pushCard({
          type: 'done',
          resumen: 'Terminó la generación en el Canvas: ya podés ver los resultados.',
          siguientes: [
            { label: 'Seguir con LMX', text: 'Ya terminó la generación en el Canvas. Seguí con el plan.' },
            { label: 'Ver en el Canvas', action: { type: 'open', target: 'canvas' } },
          ],
        });
      }
    };
    window.addEventListener('sml:mcp-run', onRun);
    return () => window.removeEventListener('sml:mcp-run', onRun);
  }, [projectId, store]);

  // Envío LMX-6b · reloj del turno (para la franja de estado).
  const [busyStart, setBusyStart] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    if (!busy) { setBusyStart(null); return undefined; }
    setBusyStart((t) => t || Date.now());
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [busy]);

  // Envío LMX-6b · si se cortó la conexión, el turno sigue en el servidor:
  // traemos la charla cada 6 s hasta que aparezca lo que hizo (máx. 1 min).
  useEffect(() => {
    let timer = null;
    const onRehydrate = () => {
      let tries = 0;
      const before = store.getState().log.length;
      const tick = async () => {
        tries += 1;
        try {
          const token = await tokenRef.current();
          const res = await fetch(`${API}/api/lmx/conversation?project_id=${projectId}`,
            { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) {
            const data = await res.json();
            if ((data.messages || []).length > before) { store.getState().hydrate(data); return; }
          }
        } catch { /* reintenta */ }
        if (tries < 10) timer = setTimeout(tick, 6000);
      };
      timer = setTimeout(tick, 6000);
    };
    window.addEventListener('lmx:rehydrate', onRehydrate);
    return () => { window.removeEventListener('lmx:rehydrate', onRehydrate); clearTimeout(timer); };
  }, [projectId, store]);

  // Envío LMX-6b · «Seguir» automático: hasta 3 pausas seguidas sin que el
  // usuario escriba. Al escribir algo, el contador vuelve a cero.
  const contStreakRef = useRef(0);

  // ── Envío LMX-T · trabajos, cola, Pausar/Detener ──
  const lmxFetch = useCallback(async (path, { method = 'GET', body } = {}) => {
    const token = await tokenRef.current();
    const res = await fetch(`${API}/api/lmx${path}`, {
      method,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.detail || `El servidor respondió ${res.status}`);
    return data;
  }, []);
  const [jobBusy, setJobBusy] = useState(() => new Set());
  const dispatchedRef = useRef(new Set());
  const afterStopRef = useRef(null);
  const submitRef = useRef(null);

  const loadJobs = useCallback(async () => {
    if (!projectId || !available) return null;
    try {
      const snap = await lmxFetch(`/jobs?project_id=${projectId}`);
      store.getState().setJobs(snap);
      // ¿Arranca el próximo pedido en espera? Sólo con LMX libre y sin nada
      // que la persona tenga que responder.
      const st = store.getState();
      const next = nextToDispatch({ snap, busy: st.busy, pendingConfirm: st.pendingConfirm,
                                    cards: st.turn.cards });
      if (next && !dispatchedRef.current.has(next.id)) {
        dispatchedRef.current.add(next.id);
        send({ message: next.request, projectId, screen: 'canvas', jobId: next.id });
      }
      return snap;
    } catch { return null; }
  }, [available, lmxFetch, projectId, send, store]);

  useEffect(() => {
    dispatchedRef.current = new Set();
    loadJobs();
  }, [projectId, loadJobs]);

  // Al cerrar cada turno: lista al día, «Detener y hacer esto», cola, y
  // recotizar si la aprobación había vencido.
  useEffect(() => {
    const onClosed = (e) => {
      if (e?.detail?.projectId && e.detail.projectId !== projectId) return;
      if (afterStopRef.current) {
        const t = afterStopRef.current;
        afterStopRef.current = null;
        setTimeout(() => submitRef.current?.(t), 250);
        return;
      }
      if (e?.detail?.expired) {
        setTimeout(() => submitRef.current?.(
          'La aprobación anterior venció. Volvé a cotizar lo mismo y pedime el OK de nuevo, '
          + 'diciéndome si cambió el costo.'), 250);
        return;
      }
      setTimeout(loadJobs, 400);
    };
    window.addEventListener('lmx:turn-closed', onClosed);
    return () => window.removeEventListener('lmx:turn-closed', onClosed);
  }, [loadJobs, projectId]);

  // Mientras haya trabajos en curso, la lista se refresca cada 15 s (una
  // lectura liviana; el estado sale de las corridas reales en el servidor).
  const hasLiveJobs = !!(jobs?.jobs || []).some((j) => JOB_ACTIVE.includes(j.status));
  const runIdsKey = (runs || []).map((r) => r.run_id).join(',');
  useEffect(() => {
    if (!hasLiveJobs) return undefined;
    const id = setInterval(loadJobs, 15000);
    return () => clearInterval(id);
  }, [hasLiveJobs, loadJobs]);
  useEffect(() => { if (projectId) loadJobs(); }, [runIdsKey]); // eslint-disable-line

  const stopTurn = useCallback(async (mode) => {
    try {
      await lmxFetch('/stop', { method: 'POST', body: { project_id: projectId, mode } });
      toast.info(mode === 'pause'
        ? 'Pauso en la próxima pausa segura (no a mitad de un paso).'
        : 'Me detengo en la próxima pausa segura. Lo hecho queda guardado.');
    } catch { toast.error('No pude avisarle a LMX. Probá de nuevo.'); }
  }, [lmxFetch, projectId]);

  const onJobAction = useCallback(async (kind, job) => {
    if (kind === 'focus') {
      window.dispatchEvent(new CustomEvent('sml:open', { detail: { target: 'canvas' } }));
      setTimeout(() => window.dispatchEvent(new CustomEvent('sml:focus-section', {
        detail: { sectionId: job.section_id } })), 150);
      return;
    }
    setJobBusy((b) => new Set(b).add(job.id));
    try {
      if (kind === 'pause') {
        const out = await lmxFetch(`/jobs/${job.id}/pause`, { method: 'POST' });
        store.getState().setJobs(out);
        toast.info(out.runs_stopped
          ? 'Pausado: lo que ya terminó queda; no se lanza nada nuevo.'
          : 'Pausado. Lo retomás cuando quieras.');
      } else if (kind === 'resume') {
        const out = await lmxFetch(`/jobs/${job.id}/resume`, { method: 'POST' });
        send({ message: out.text, projectId, screen: 'canvas', jobId: job.id });
      } else if (kind === 'remove') {
        await lmxFetch(`/jobs/${job.id}`, { method: 'DELETE' });
        await loadJobs();
      }
    } catch (err) {
      toast.error(err.message || 'No se pudo hacer.');
    } finally {
      setJobBusy((b) => { const n = new Set(b); n.delete(job.id); return n; });
    }
  }, [lmxFetch, loadJobs, projectId, send, store]);

  // ── Permisos de ejecución (antes sólo existía el switch en el panel viejo:
  //    desde el dock no había forma de verlo ni de cambiarlo) ──
  const execOn = !!prefs?.exec_permission;
  const toggleExec = useCallback(async () => {
    const next = !execOn;
    store.getState().setPrefs({ exec_permission: next });
    try {
      const token = await tokenRef.current();
      const res = await fetch(`${API}/api/lmx/me/prefs`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ exec_permission: next }),
      });
      if (!res.ok) throw new Error(String(res.status));
      toast.success(next
        ? 'LMX ya puede crear y cambiar nodos. Gastar créditos o borrar siempre te lo pregunta.'
        : 'LMX queda en solo lectura: puede mirar y cotizar, no cambiar nada.');
    } catch {
      store.getState().setPrefs({ exec_permission: !next });
      toast.error('No se pudo cambiar el permiso de LMX. Probá de nuevo.');
    }
  }, [execOn, store]);

  // ── Acciones (mismas que LmxLayer) ──
  const submit = useCallback((value, jobId) => {
    const v = (value || '').trim();
    const st = store.getState();
    const atts = st.attachments.filter((a) => a.status === 'ready');
    if (!v && !atts.length) return;
    // Envío LMX-T · con LMX trabajando, el pedido queda en espera (no arranca
    // otro turno). Las imágenes se adjuntan cuando termine.
    if (st.busy) {
      if (atts.length) { toast.info('Esperá a que LMX termine para adjuntar imágenes.'); return; }
      send({ message: v, projectId });
      return;
    }
    if (st.attachments.some((a) => a.status === 'uploading')) {
      toast.info('Esperá a que termine de subir la imagen.');
      return;
    }
    st.clearAttachments();
    st.markGreeted();
    // Timeline en vivo: con el editor abierto se FLUSHEA el autosave ANTES de
    // mandar el mensaje: LMX parte del doc fresco y el conflicto queda sólo
    // para edición simultánea real. Aunque el guardado falle, se manda igual.
    if (timelineOpen && projectId) {
      Promise.resolve(useTimelineStore.getState().saveNow(projectId))
        .catch(() => {})
        .finally(() => send({ message: v, projectId, screen: 'timeline', attachments: atts, jobId }));
      return;
    }
    send({ message: v, projectId, screen: 'canvas', attachments: atts, jobId });
  }, [projectId, send, store, timelineOpen]);
  submitRef.current = submit;

  const onAction = useCallback((action) => {
    if (!action) return;
    if (action.type === 'message') { submit(action.text, action.job_id); return; }
    // Envío LMX-T · opciones de «ya hay 2 trabajos».
    if (action.type === 'queue' && action.text) {
      lmxFetch('/jobs/queue', { method: 'POST', body: { project_id: projectId, text: action.text } })
        .then((out) => { store.getState().setJobs(out); toast.success('Lo anoté: arranca apenas termine uno.'); })
        .catch((err) => toast.error(err.message || 'No pude anotarlo.'));
      return;
    }
    if (action.type === 'pause_and_run' && action.job_id) {
      lmxFetch(`/jobs/${action.job_id}/pause`, { method: 'POST' })
        .then((out) => { store.getState().setJobs(out); submit(action.text); })
        .catch((err) => toast.error(err.message || 'No pude pausar ese trabajo.'));
      return;
    }
    if (action.type === 'focus_node') { requestFocus(action.node_id); return; }
    if (action.type === 'open_project' && action.project_id) {
      const ws = useWorkflowStore.getState();
      if (ws.hasUnsavedChanges
        && !window.confirm('Tenés cambios sin guardar en este proyecto. ¿Abrir el otro igual y descartarlos?')) return;
      ws.loadProject(action.project_id);
      return;
    }
    // Envío LMX-5: botones de la guía → abren la parte de la plataforma.
    if (action.type === 'open' && action.target) {
      // AP-5: `tab` elige la pestaña (Biblioteca › Marcas desde la tarjeta de marca).
      window.dispatchEvent(new CustomEvent('sml:open', { detail: { target: action.target, tab: action.tab } }));
      return;
    }
    if (action.type === 'approve') { approve(action.confirm_id, projectId); return; }
    if (action.type === 'cancel') { cancel(action.confirm_id, projectId); }
  }, [approve, cancel, lmxFetch, projectId, requestFocus, store, submit]);

  // ── Manija: arrastrar el borde superior cambia el alto del cuerpo ──
  const startDrag = (e) => {
    e.preventDefault();
    if (!open) store.getState().open();
    dragRef.current = { y: e.clientY, h: bodyH };
    const move = (ev) => {
      const max = Math.round(window.innerHeight * H_MAX_RATIO);
      const next = Math.min(max, Math.max(H_MIN, dragRef.current.h + (dragRef.current.y - ev.clientY)));
      setBodyH(next);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      setBodyH((h) => { lsSet(H_KEY, String(h)); return h; });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // El cuerpo nunca pasa por debajo del encabezado del Studio: con el timeline
  // estirado se achica a lo que queda libre.
  // Si el timeline ocupa casi todo, el dock se apoya sobre él (lo pisa un poco)
  // en vez de esconderse debajo del encabezado.
  const winH = typeof window !== 'undefined' ? window.innerHeight : 900;
  const headerBottom = (typeof document !== 'undefined'
    && document.querySelector('.studio-main')?.getBoundingClientRect().top) || 110;
  const tlOffset = tlHeight ? tlHeight + TL_HANDLE_GAP : 0;
  const roomAboveTl = winH - headerBottom - tlOffset - STRIP_H - 12;
  const effBodyH = Math.max(120, Math.min(bodyH, Math.max(roomAboveTl, Math.round(winH * 0.45))));
  const dockH = STRIP_H + (open ? effBodyH : 0) + 8;
  const dockBottom = Math.max(0, Math.min(tlOffset, winH - headerBottom - dockH));

  // Envío LMX-6 · semáforo: qué necesita LMX de la persona, en un solo valor.
  const attention = lmxAttention({ busy, pendingConfirm, cards: turn.cards });
  // Envío LMX-6b · franja de estado: qué está haciendo, hace cuánto y en qué paso.
  const progressItems = turn.cards.filter((c) => c?.type === 'progress').flatMap((c) => c.items || []);
  const runningItem = [...progressItems].reverse().find((it) => it.state === 'go');
  const statusText = statusLine({
    attention, busy, toolLabel: runningItem?.label, statusText: status?.text, run: runInfo,
  });
  const showStatus = busy || !!runInfo || ['question', 'approve', 'continue', 'blocked'].includes(attention);
  useEffect(() => {
    const root = document.body;
    root.dataset.lmxAttention = attention;
    const needs = attention === 'question' || attention === 'approve';
    const base = document.title.replace(/^\(\d\) LMX [^·]+· /, '');
    document.title = needs
      ? `(1) LMX ${attention === 'question' ? 'te pregunta' : 'espera tu OK'} · ${base}`
      : base;
    return () => { delete root.dataset.lmxAttention; };
  }, [attention]);
  if (!available) return null;

  // En el renglón plegado va sólo el ÚLTIMO párrafo de LMX (lo más reciente).
  const lastText = turn.text?.trim()
    || [...log].reverse().find((m) => m.who !== 'Vos')?.text
    || 'Contame qué querés crear.';
  const lastLine = String(lastText).trim().split(/\n{2,}/).pop();
  const state = pendingConfirm ? 'confirm' : busy ? 'busy' : notify ? 'notify' : 'idle';


  // ── Envío UI-2 · columna fija a la derecha ──────────────────────────────
  // Misma lógica (store, turnos, adjuntos, permisos, sincronización); sólo
  // cambia la forma: cabecera, conversación con tarjetas, y el campo abajo
  // con las imágenes adjuntas ARRIBA del texto. Plegada queda como un riel.
  if (LAYOUT_V2) {
    const sendNow = () => {
      contStreakRef.current = 0;
      stopMic(); submit(text); setText(''); speechBaseRef.current = '';
    };
    // Envío LMX-T · lo nuevo pasa adelante: LMX frena en la próxima pausa
    // segura y arranca con este pedido.
    const stopAndDo = () => {
      const v = text.trim();
      if (!v) return;
      contStreakRef.current = 0;
      stopMic(); setText(''); speechBaseRef.current = '';
      afterStopRef.current = v;
      stopTurn('stop');
    };
    const activeJobs = jobs?.active || 0;
    return (
      <>
        {mode === 'curtain' && (
          <LmxDockEntry image={assets.curtain_bg} orb={assets.orb} onDone={leaveCurtain} />
        )}
        <aside
          className={`lmx-col ${open ? 'is-open' : 'is-rail'}`}
          ref={dockRef}
          data-state={state}
          data-attention={attention}
          aria-label="LMX, agente de Studio Media Labs"
          data-testid="lmx-dock"
        >
          {/* I-44 · lengüeta en el borde: «‹‹» abre, «››» pliega. */}
          <button
            type="button"
            className="lmx-col-tab"
            onClick={() => (open ? store.getState().collapse() : store.getState().open())}
            aria-expanded={open}
            aria-label={open ? 'Minimizar LMX' : 'Abrir LMX'}
            title={open ? 'Minimizar LMX (Esc)' : 'Abrir LMX'}
            data-testid="lmx-col-tab"
          >
            {open ? <ChevronsRight size={13} /> : <ChevronsLeft size={13} />}
          </button>
          {!open ? (
            /* I-44 · toda la barra plegada abre LMX (no sólo el orbe). */
            <button
              type="button"
              className="lmx-col-rail"
              onClick={() => store.getState().open()}
              aria-expanded={false}
              aria-label="Abrir LMX"
              title="Abrir LMX"
              data-testid="lmx-col-rail"
            >
              <span className="lmx-dock-orb lmx-col-orb" data-testid="lmx-dock-orb">
                <img src={assets.orb} alt="" />
                <span className="lmx-dock-dot" />
              </span>
              {(pendingConfirm || notify || ['question', 'done', 'blocked', 'continue'].includes(attention)) && (
                <span className={`lmx-col-rail-alert is-${attention}`} aria-label={ATTENTION_LABEL[attention] || 'LMX tiene algo para vos'}>
                  {ATTENTION_ICON[attention] || '•'}
                </span>
              )}
              <span className="lmx-col-rail-label">LMX</span>
            </button>
          ) : (
            <>
              <header className="lmx-col-head">
                <button
                  type="button"
                  className="lmx-dock-orb lmx-col-orb"
                  onClick={() => store.getState().collapse()}
                  aria-expanded
                  aria-label="Plegar LMX"
                  data-testid="lmx-dock-orb"
                >
                  <img src={assets.orb} alt="" />
                  <span className="lmx-dock-dot" />
                </button>
                <div className="lmx-col-title">
                  <b>LMX</b>
                  <span aria-live="polite">
                    {busy ? (status.text || 'Trabajando…')
                      : activeJobs ? `${activeJobs} trabajo${activeJobs > 1 ? 's' : ''} en curso · ${timelineOpen ? 'Edición' : 'Canvas'}`
                        : timelineOpen ? 'Escuchando · Edición' : 'Escuchando · Canvas'}
                  </span>
                </div>
                <button
                  type="button"
                  className={`lmx-dock-chip ${execOn ? '' : 'is-warn'}`}
                  aria-pressed={execOn}
                  onClick={toggleExec}
                  title={execOn
                    ? 'LMX puede crear y cambiar nodos. Lo que gasta créditos siempre te lo pregunta antes. Tocá para dejarlo en solo lectura.'
                    : 'LMX sólo puede leer y cotizar. Tocá para que pueda crear y cambiar nodos.'}
                  data-testid="lmx-dock-perm"
                >
                  {execOn ? 'Puede editar' : 'Solo lectura'}
                </button>
              </header>

              {showStatus && (
                <div className={`lmx-col-status is-${busy ? 'working' : runInfo ? 'run' : attention}`}
                     role="status" aria-live="polite" data-testid="lmx-col-status">
                  <span className="lmx-col-status-wave" aria-hidden><i /><i /><i /><i /></span>
                  <span className="lmx-col-status-text">{statusText}</span>
                  {busy && (
                    <span className="lmx-col-status-meta">
                      {progressItems.length > 0 && `paso ${progressItems.length} · `}
                      {fmtElapsed(busyStart ? nowTick - busyStart : 0)}
                    </span>
                  )}
                  {busy && (
                    <span className="lmx-col-status-ctl">
                      <button type="button" onClick={() => stopTurn('pause')} title="Pausar en la próxima pausa segura" data-testid="lmx-pause">Pausar</button>
                      <button type="button" onClick={() => stopTurn('stop')} title="Detener (lo hecho queda guardado)" data-testid="lmx-stop">Detener</button>
                    </span>
                  )}
                  {!busy && runInfo && runInfo.total > 0 && (
                    <span className="lmx-col-status-bar" aria-hidden>
                      <i style={{ width: `${Math.round((runInfo.done / runInfo.total) * 100)}%` }} />
                    </span>
                  )}
                </div>
              )}
              <LmxJobs snap={jobs} runs={runs} onAction={onJobAction} busyIds={jobBusy} />
              {pendingConfirm && (
                <div className="lmx-dock-await lmx-col-await" role="status" data-testid="lmx-dock-await">
                  <span className="lmx-dock-await-text">
                    {pendingConfirm.summary || 'LMX espera tu aprobación'}
                    {pendingConfirm.cost > 0 && <b> · {pendingConfirm.cost} cr</b>}
                  </span>
                  <button type="button" className="is-primary"
                          onClick={() => onAction({ type: 'approve', confirm_id: pendingConfirm.id })}>
                    Aprobar
                  </button>
                </div>
              )}

              <div className="lmx-col-convo" ref={convoRef} onScroll={onConvoScroll} data-testid="lmx-dock-body">
                {!log.length && !busy && (
                  <>
                    <p className="lmx-dock-empty">Contame qué querés crear. Podés adjuntar hasta 4 imágenes de referencia.</p>
                    {/* Envío LMX-5: lo que nadie descubre solo, a un toque. */}
                    <div className="lmx-col-starters" data-testid="lmx-starters">
                      {LMX_STARTERS.map((s) => (
                        <button key={s.label} type="button" onClick={() => submit(s.text)}>{s.label}</button>
                      ))}
                    </div>
                  </>
                )}
                {log.map((m, i) => (
                  <div key={i} className={`lmx-dock-msg ${m.who === 'Vos' ? 'is-me' : ''}`}>
                    {m.who === 'Vos' ? m.text : <LmxText text={m.text} />}
                    {!!m.images?.length && (
                      <div className="lmx-dock-msg-imgs">
                        {m.images.map((src, j) => <img key={j} src={src} alt="" />)}
                      </div>
                    )}
                  </div>
                ))}
                {busy && turn.text && <div className="lmx-dock-msg"><LmxText text={turn.text} /></div>}
                {busy && !turn.text && <div className="lmx-typing"><i /><i /><i /></div>}
                {turn.cards.filter((c) => !isEmptyCard(c)).map((card, i) => (
                  <div className="lmx-dock-card" key={`c${i}`}>
                    {card.type === 'continue' ? (
                      <LmxCard
                        card={{ ...card, auto: !!card.live && !busy && card.progress !== false && contStreakRef.current < 3 }}
                        onAction={(a) => { contStreakRef.current += 1; onAction(a); }}
                      />
                    ) : <LmxCard card={card} onAction={onAction} />}
                  </div>
                ))}
                {pendingConfirm && (
                  <div className="lmx-dock-card lmx-confirm" data-testid="lmx-dock-confirm">
                    <p className="lmx-confirm-sum">{pendingConfirm.summary}</p>
                    {pendingConfirm.cost > 0 && (
                      <p className="lmx-confirm-cost">Cuesta <strong>{pendingConfirm.cost} créditos</strong></p>
                    )}
                    <div className="lmx-dock-btns">
                      <button type="button" className="is-primary"
                              onClick={() => onAction({ type: 'approve', confirm_id: pendingConfirm.id })}>
                        Aprobar
                      </button>
                      <button type="button"
                              onClick={() => onAction({ type: 'cancel', confirm_id: pendingConfirm.id })}>
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <form
                className={`lmx-col-ask ${busy || pendingConfirm || ['question', 'approve', 'continue'].includes(attention) ? 'is-quiet' : ''}`}
                onSubmit={(e) => { e.preventDefault(); sendNow(); }}
                onDragOver={attach.onDragOver}
                onDrop={attach.onDrop}
                autoComplete="off"
              >
                <LmxAttachTray />
                <textarea
                  rows={2}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onPaste={attach.onPaste}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                      e.preventDefault();
                      sendNow();
                    }
                  }}
                  placeholder={attachments.length ? 'Contale qué hacer con la imagen…' : 'Pedile algo a LMX…'}
                  aria-label="Pedile algo a LMX"
                  data-testid="lmx-dock-input"
                />
                <div className="lmx-col-ask-row">
                  <button
                    type="button"
                    className={`lmx-dock-mic ${speech.listening ? 'on' : ''}`}
                    onClick={toggleMic}
                    aria-pressed={speech.listening}
                    title={speech.listening ? 'Cortar el dictado' : 'Dictarle a LMX por voz'}
                    data-testid="lmx-dock-mic"
                  >
                    {speech.listening ? <MicOff size={14} /> : <Mic size={14} />}
                  </button>
                  <LmxAttachButton onFiles={attach.addFiles} disabled={busy} />
                  <button type="button" className="lmx-dock-chip lmx-col-gotonode" onClick={() => setSearchOpen(true)} title="Ir a un nodo del canvas">
                    Ir al nodo <kbd>Ctrl K</kbd>
                  </button>
                  <span className="lmx-col-spacer" />
                  {busy && text.trim() && (
                    <button type="button" className="lmx-col-stopdo" onClick={stopAndDo}
                            title="LMX frena lo que está haciendo y arranca con esto"
                            data-testid="lmx-stop-and-do">
                      Detener y hacer esto
                    </button>
                  )}
                  <button
                    type="submit"
                    className="lmx-col-send"
                    disabled={(!text.trim() && !readyAtts.length) || uploadingAtts || (busy && !text.trim())}
                    title={busy ? 'LMX está trabajando: tu pedido queda en espera y arranca cuando termine' : undefined}
                    data-testid="lmx-dock-send"
                  >
                    <Send size={13} /> {busy ? 'Anotar' : 'Enviar'}
                  </button>
                </div>
              </form>
            </>
          )}
        </aside>
      </>
    );
  }

  return (
    <>
      {mode === 'curtain' && (
        <LmxDockEntry image={assets.curtain_bg} orb={assets.orb} onDone={leaveCurtain} />
      )}

      {open && veil && (
        <div className="lmx-dock-veil" style={{ bottom: dockBottom }} aria-hidden />
      )}

      <section
        className={`lmx-dock ${open ? 'is-open' : 'is-collapsed'} ${mode === 'curtain' ? 'is-entry' : ''}`}
        ref={dockRef}
        style={{ bottom: dockBottom, '--lmx-dock-body': `${effBodyH}px`, '--lmx-strip': `${STRIP_H}px` }}
        data-state={state}
        aria-label="LMX, agente de Studio Media Labs"
        data-testid="lmx-dock"
      >
        <div
          className="lmx-dock-handle"
          onPointerDown={startDrag}
          role="separator"
          aria-orientation="horizontal"
          aria-label="Cambiar el alto de LMX"
          title="Arrastrá para cambiar el alto"
        />

        {/* Espera tu aprobación: aviso visible siempre, plegado o desplegado */}
        {pendingConfirm && (
          <div className="lmx-dock-await" role="status" data-testid="lmx-dock-await">
            <span className="lmx-dock-await-arrow" aria-hidden>
              <svg viewBox="0 0 16 16"><path d="M2 8h10M8 3.5 12.5 8 8 12.5" /></svg>
            </span>
            <span className="lmx-dock-await-text">
              {pendingConfirm.summary || 'LMX espera tu aprobación'}
              {pendingConfirm.cost > 0 && <b> · {pendingConfirm.cost} cr</b>}
            </span>
            <button type="button" className="is-primary"
                    onClick={() => onAction({ type: 'approve', confirm_id: pendingConfirm.id })}>
              Aprobar
            </button>
            <button
              type="button"
              onClick={() => {
                store.getState().open();
                setTimeout(() => document.querySelector('[data-testid="lmx-dock-confirm"]')
                  ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 60);
              }}
            >
              Revisar
            </button>
          </div>
        )}

        {pendingConfirm && (
          <span className="lmx-dock-alert" aria-label="LMX te hace una pregunta">?</span>
        )}

        {/* cuna: el marco se amolda al orbe */}
        <div className="lmx-dock-cradle">
          <button
            type="button"
            className="lmx-dock-orb"
            onClick={() => store.getState().toggle()}
            aria-expanded={open}
            aria-label={open ? 'Plegar LMX' : 'Desplegar LMX'}
            data-testid="lmx-dock-orb"
          >
            <img src={assets.orb} alt="" />
            <span className="lmx-dock-dot" />
          </button>
        </div>

        <div
          className="lmx-dock-strip"
          onDoubleClick={(e) => { if (!e.target.closest('input,button')) store.getState().toggle(); }}
        >
          <p className="lmx-dock-say" aria-live="polite" key={lastLine}>
            <b>LMX</b>{busy && !turn.text ? status.text : <LmxText text={lastLine} inline />}
          </p>
          <button
            type="button"
            className={`lmx-dock-mic ${speech.listening ? 'on' : ''}`}
            onClick={toggleMic}
            aria-pressed={speech.listening}
            title={speech.listening ? 'Cortar el dictado' : 'Dictarle a LMX por voz'}
            data-testid="lmx-dock-mic"
          >
            {speech.listening ? <MicOff size={14} /> : <Mic size={14} />}
          </button>
          <LmxAttachButton onFiles={attach.addFiles} disabled={busy} />
          <form
            className="lmx-dock-ask"
            onSubmit={(e) => { e.preventDefault(); stopMic(); submit(text); setText(''); speechBaseRef.current = ''; }}
            onDragOver={attach.onDragOver}
            onDrop={attach.onDrop}
            autoComplete="off"
          >
            <LmxAttachTray />
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onPaste={attach.onPaste}
              placeholder={attachments.length ? 'Contale qué hacer con la imagen…' : 'Pedile algo a LMX…'}
              aria-label="Pedile algo a LMX"
              data-testid="lmx-dock-input"
            />
            <button type="submit" disabled={(!text.trim() && !readyAtts.length) || busy || uploadingAtts}>Enviar</button>
          </form>
          <div className="lmx-dock-ctl">
            <button
              type="button"
              className={`lmx-dock-chip ${execOn ? '' : 'is-warn'}`}
              aria-pressed={execOn}
              onClick={toggleExec}
              title={execOn
                ? 'LMX puede crear y cambiar nodos. Lo que gasta créditos siempre te lo pregunta antes. Tocá para dejarlo en solo lectura.'
                : 'LMX sólo puede leer y cotizar. Tocá para que pueda crear y cambiar nodos.'}
              data-testid="lmx-dock-perm"
            >
              {execOn ? 'Puede editar' : 'Solo lectura'}
            </button>
            <button type="button" className="lmx-dock-chip" aria-pressed={veil}
                    onClick={() => store.getState().setVeil(!veil)}>Velo</button>
            <button type="button" className="lmx-dock-chip" onClick={() => setSearchOpen(true)}>
              Ir al nodo <kbd>Ctrl K</kbd>
            </button>
            {LMX_TOUR && (
            <button
              type="button"
              className="lmx-dock-chip"
              onClick={() => window.dispatchEvent(new CustomEvent('lmx:tour', { detail: {} }))}
              title="Ver el recorrido del Studio"
              data-testid="lmx-dock-tour-chip"
            >
              Recorrido
            </button>
            )}
            <button type="button" className="lmx-dock-chip" aria-pressed={pinned}
                    onClick={() => setPinned((p) => !p)} title="Fijar abierto aunque se agrande el timeline">
              Fijar
            </button>
            <button type="button" className="lmx-dock-chip lmx-dock-chev"
                    onClick={() => store.getState().toggle()} aria-label={open ? 'Plegar' : 'Desplegar'}>
              {open ? '▾' : '▴'}
            </button>
          </div>
        </div>

        {open && (
          <div className="lmx-dock-body" data-testid="lmx-dock-body">
            <div className="lmx-dock-convo" ref={convoRef}>
              {log.map((m, i) => (
                <div key={i} className={`lmx-dock-msg ${m.who === 'Vos' ? 'is-me' : ''}`}>
                  {m.who === 'Vos' ? m.text : <LmxText text={m.text} />}
                  {!!m.images?.length && (
                    <div className="lmx-dock-msg-imgs">
                      {m.images.map((src, j) => <img key={j} src={src} alt="" />)}
                    </div>
                  )}
                </div>
              ))}
              {busy && turn.text && <div className="lmx-dock-msg"><LmxText text={turn.text} /></div>}
              {busy && !turn.text && <div className="lmx-typing"><i /><i /><i /></div>}
            </div>
            <div className="lmx-dock-cards">
              {turn.cards.filter((c) => !isEmptyCard(c)).map((card, i) => (
                <div className="lmx-dock-card" key={i}><LmxCard card={card} onAction={onAction} /></div>
              ))}
              {pendingConfirm && (
                <div className="lmx-dock-card lmx-confirm" data-testid="lmx-dock-confirm">
                  <p className="lmx-confirm-sum">{pendingConfirm.summary}</p>
                  {pendingConfirm.cost > 0 && (
                    <p className="lmx-confirm-cost">Cuesta <strong>{pendingConfirm.cost} créditos</strong></p>
                  )}
                  <div className="lmx-dock-btns">
                    <button type="button" className="is-primary"
                            onClick={() => onAction({ type: 'approve', confirm_id: pendingConfirm.id })}>
                      Aprobar
                    </button>
                    <button type="button"
                            onClick={() => onAction({ type: 'cancel', confirm_id: pendingConfirm.id })}>
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
              {!turn.cards.some((c) => !isEmptyCard(c)) && !pendingConfirm && (
                <p className="lmx-dock-empty">Acá aparecen el costo, el flujo y los resultados de cada pedido.</p>
              )}
            </div>
          </div>
        )}
      </section>
    </>
  );
};

export default LmxDock;
